<template>
  <div class="h-full bg-gray-50 border-l">
    <div v-if="row" class="h-full flex flex-col">
      <!-- Enhanced Header -->
      <div class="bg-gray-50 px-6 py-3 border-b border-gray-100 flex items-center shadow-sm justify-between">
        <div class="flex items-center gap-2">
          <div class="text-md font-bold">{{ row.task_type_name }}</div>
        </div>
        <button @click="close">
          <FontAwesomeIcon :icon="['fas','xmark']"/>
        </button>
      </div>

      <!-- Scrollable Content Area -->
      <div class="flex-1 overflow-y-auto p-6 space-y-6">
        <!-- Snooze Alert -->
        <div v-if="row.status === TaskStatus.SNOOZED"
             class="bg-yellow-light border border-amber-200 rounded-lg p-4 shadow-sm">
          <div class="flex justify-between items-start">
            <div class="flex items-start space-x-4">
              <div class="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center shrink-0">
                <FontAwesomeIcon :icon="['fas', 'clock']" class="text-amber-600" />
              </div>
              <div class="flex-1">
                <h3 class="font-semibold text-amber-900">Task is Snoozed</h3>
                <div v-if="row.metadata.snooze_until" class="text-sm text-amber-700 mt-1">
                  <p>Snoozed until: <span class="font-medium">{{ formatDate(row.metadata.snooze_until) }}</span></p>
                </div>
                <div v-if="row.metadata.snoozed_by" class="text-sm text-amber-700 mt-1">
                  <p>By: <span class="font-medium">{{ row.metadata.snoozed_by }}</span></p>
                </div>
              </div>
            </div>
            <button
              @click="triggerUnsnooze"
              :disabled="unsnoozing"
              class="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400
                     text-white rounded-lg text-sm font-medium transition-all duration-200
                     transform hover:scale-105 disabled:scale-100"
            >
              <span v-if="unsnoozing" class="flex items-center">
                <FontAwesomeIcon :icon="['fas', 'spinner']" class="animate-spin mr-2" />
                Unsnoozing...
              </span>
              <span v-else>Unsnooze Now</span>
            </button>
          </div>
        </div>



        <!-- Related Opportunities Section -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
          <div class="bg-primary-50 px-5 py-3 border-b border-gray-100">
            <h3 class="font-semibold text-gray-900 flex items-center">
              <FontAwesomeIcon :icon="['fas', 'bolt']" class="mr-2 text-primary-600" />
              Related Opportunities
            </h3>
          </div>
          <div class="p-4 space-y-4">
            <div v-for="opportunity in row.opportunities" :key="opportunity.id"
                 class="transform transition-all duration-200 hover:translate-x-1">
              <Opportunity_Widget :opportunity="opportunity" />
            </div>
          </div>
        </div>

                <!-- Task Owner Widget -->
        <Task_Owner_Widget
          :task="row"
          @owner-updated="handleOwnerUpdate"
        />

        <!-- Task Information Section -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
          <div class="bg-primary-50 px-5 py-3 border-b border-gray-100">
            <h3 class="font-semibold text-gray-900 flex items-center">
              <FontAwesomeIcon :icon="['fas', 'circle-info']" class="mr-2 text-purple-600" />
              Task Information
            </h3>
          </div>
          <div class="p-5">
            <p class="text-sm text-gray-600 leading-relaxed">{{ row.task_type_description }}</p>

            <div class="">
              <Task_Side_Panel_Widget
                :task="row"
                @can_submit="set_can_submit"
              />
            </div>
          </div>
        </div>

        <!-- Work Activities Section -->
        <div v-if="hasWorkActivities" class="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
          <div class="bg-gray-50 px-5 py-3 border-b border-gray-100">
            <h3 class="font-semibold text-gray-900 flex items-center">
              <FontAwesomeIcon :icon="['fas', 'clipboard-check']" class="mr-2 text-gray-600" />
              Work Activities
            </h3>
          </div>
          <div class="p-5 space-y-5">
            <!-- Work Logger Section -->
            <div class="border-b border-gray-100 pb-5">
              <WorkLogger
                :opportunities="row.opportunities"
                :task-id="row.id"
                :task-type-id="row.task_type"
                :task-metadata="row.metadata"
                @work-submitted="$refs.workTimeline?.fetchWorkItems()"
              />
            </div>

            <!-- Work Timeline Section -->
            <div>
              <h4 class="text-sm font-medium text-gray-700 mb-3">Activity History</h4>
              <Work_Timeline
                ref="workTimeline"
                :task-id="row.id"
                :task-type-id="row.task_type"
                :task-type-name="row.task_type_name"
                :task-metadata="row.metadata || {}"
                :opportunities="row.opportunities"
              />
            </div>
          </div>
        </div>

        <!-- Action Selection Section -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-100 p-5">
          <label class="block text-sm font-semibold text-gray-900 mb-3 flex items-center">
            <FontAwesomeIcon :icon="['fas', 'clipboard-list']" class="mr-2 text-primary-600" />
            Select Action
          </label>
          <select
            v-model="selectedAction"
            class="w-full px-4 py-2.5 border border-gray-300 rounded-lg
                   focus:ring-2 focus:ring-blue-dark focus:border-transparent
                   hover:border-gray-400 transition-colors duration-200"
          >
            <option disabled value="">Please select an action</option>
            <option v-for="action in activeActions" :key="action.id" :value="action">
              {{ action.name }}
            </option>
          </select>

          <!-- Date Picker - Show for Snooze (type 6) and UpdateDueDate (type 16) -->
          <div v-if="selectedAction && NEEDS_DATE.includes(selectedAction.task_instruction_type_id)"
               class="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              {{ selectedAction.task_instruction_type_id === InstructionType.UPDATE_DUE_DATE ? 'New Due Date' : 'Snooze Until' }}
            </label>
            <div class="flex items-center gap-1.5">
              <button
                v-for="opt in [{days:1,label:'1d'},{days:3,label:'3d'},{days:7,label:'7d'}]"
                :key="opt.days"
                class="px-2 py-1.5 rounded text-xs font-medium transition-colors"
                :class="daysSelected === opt.days ? 'bg-gray-900 text-white' : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-100'"
                @click="setQuickDate(opt.days)"
              >{{ opt.label }}</button>
              <div class="relative flex-1">
                <button
                  class="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs text-left bg-white text-gray-700 hover:border-gray-400 transition-colors"
                  @click="showCalendarPicker = !showCalendarPicker"
                >{{ snoozeUntil ? formatPickerDisplay(snoozeUntil) : 'Pick a date...' }}</button>
                <div
                  v-if="showCalendarPicker"
                  class="absolute top-full left-0 mt-1 z-50 shadow-lg rounded-lg"
                  style="width: 280px;"
                >
                  <TaskCalendarPicker
                    :model-value="snoozeUntil"
                    @update:model-value="onCalendarPick"
                    @day-picked="showCalendarPicker = false"
                  />
                </div>
              </div>
            </div>
            <p v-if="!snoozeUntil" class="mt-2 text-sm text-amber-600">
              {{ selectedAction.task_instruction_type_id === InstructionType.UPDATE_DUE_DATE
                ? 'Please select a new due date for this task'
                : 'Please select a date and time to snooze this task until' }}
            </p>
          </div>
        </div>

        <!-- Additional Notes Section -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-100 p-5">
          <label class="block text-sm font-semibold text-gray-900 mb-3 flex items-center">
            <FontAwesomeIcon :icon="['fas', 'message']" class="mr-2 text-green-600" />
            Additional Notes
          </label>

          <!-- Previous Notes -->
          <div v-if="row?.metadata?.previous_task_completed_notes?.length > 0" class="space-y-3 mb-4">
            <template v-for="(note, index) in row.metadata.previous_task_completed_notes" :key="`note-${index}`">
              <div v-if="note.data"
                   class="bg-primary-50 rounded-lg p-4 border border-blue-100">
                <p class="text-sm text-gray-700">{{ note.data }}</p>
                <div class="mt-2 flex items-center text-xs text-gray-500">
                  <FontAwesomeIcon :icon="['fas', 'user']" class="mr-1" />
                  <span class="font-medium text-gray-700">{{ note.appuser }}</span>
                  <span class="mx-2">•</span>
                  <span>{{ note.created_at }}</span>
                </div>
              </div>
            </template>
          </div>

          <textarea
            v-model="additionalData"
            class="w-full px-4 py-3 border border-gray-300 rounded-lg
                   focus:ring-2 focus:ring-blue-dark focus:border-transparent
                   hover:border-gray-400 transition-colors duration-200
                   placeholder-gray-400 resize-none"
            placeholder="Enter additional notes about this task..."
            rows="4"
          ></textarea>
        </div>

        <!-- Error Display -->
        <Field_Errors :received_errors="field_errors" />

        <!-- Action History Section -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
          <div class="px-5 py-3 border-b border-gray-100">
            <button
              v-if="!openHistory"
              @click="openHistory = true"
              class="flex items-center justify-between w-full group"
            >
              <span class="text-sm font-semibold text-gray-900 flex items-center">
                <FontAwesomeIcon :icon="['fas', 'clock-rotate-left']" class="mr-2 text-gray-600" />
                Action History
              </span>
              <FontAwesomeIcon :icon="['fas', 'chevron-right']"
                class="text-gray-400 group-hover:text-gray-600 transform group-hover:translate-x-1 transition-all duration-200" />
            </button>
            <div v-else class="flex items-center justify-between">
              <span class="text-sm font-semibold text-gray-900 flex items-center">
                <FontAwesomeIcon :icon="['fas', 'clock-rotate-left']" class="mr-2 text-gray-600" />
                Action History
              </span>
              <button
                @click="openHistory = false"
                class="text-sm text-gray-500 hover:text-gray-700"
              >
                <FontAwesomeIcon :icon="['fas', 'xmark']" />
              </button>
            </div>
          </div>
          <div v-if="openHistory" class="p-4">
            <Task_Actions_List :task-prior-actions="row.task_prior_actions"/>
          </div>
        </div>
      </div>

      <!-- Fixed Footer Actions -->
      <div class="bg-white border-t border-gray-200 px-6 py-4 shadow-lg">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-4">
            <button
              @click="submit"
              :disabled="!canSubmit"
              class="px-6 py-2.5 bg-primary-500
                     hover:from-primary-700 hover:to-purple-700
                     disabled:from-gray-400 disabled:to-gray-500
                     text-white rounded-lg font-medium transition-all duration-200
                     transform hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed
                     shadow-md hover:shadow-lg flex items-center"
            >
              <FontAwesomeIcon v-if="submitting" :icon="['fas', 'spinner']" class="animate-spin mr-2" />
              <FontAwesomeIcon v-else :icon="['fas', 'check']" class="mr-2" />
              {{ submitting ? 'Submitting...' : 'Submit' }}
            </button>
            <button
              @click="close"
              class="px-6 py-2.5 bg-white border border-gray-300 text-gray-700
                     rounded-lg font-medium hover:bg-gray-50 hover:border-gray-400
                     transition-all duration-200"
            >
              Cancel
            </button>
          </div>

          <div v-if="!can_submit && selectedAction" class="text-sm text-amber-600 flex items-center">
            <FontAwesomeIcon :icon="['fas', 'circle-exclamation']" class="mr-1" />
            {{ can_submit_message }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapGetters} from 'vuex';
import Task_Actions_List from '@/components/Portal/Task_Tables/Task_Actions_List.vue';
import Opportunity_Widget from '@/components/Portal/Task_Tables/Opportunity_Widget.vue';
import Task_Side_Panel_Widget from '@/components/Portal/Task_Tables/Task_Widget_Parent.vue';
import Task_Owner_Widget from '@/components/Portal/Task_Tables/Task_Owner_Widget.vue';
import Field_Errors from '@/components/widgetcomponents/Field_Errors.vue';
import {formatDate} from "@/utils/common";
import { submitAction } from '@/services/api/tasks';
import { InstructionType, TaskStatus, NEEDS_DATE } from '@/constants/instructionTypes';
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import WorkLogger from "@/components/Portal/Task_Tables/Work_Widgets/WorkLogger.vue";
import Work_Timeline from "@/components/Portal/Task_Tables/Work_Widgets/Work_Timeline.vue";
import TaskCalendarPicker from '@/components/shared/TaskCalendarPicker.vue';

export default {
  components: {
    Work_Timeline,
    WorkLogger,
    FontAwesomeIcon,
    Field_Errors,
    Task_Side_Panel_Widget,
    Task_Owner_Widget,
    Opportunity_Widget,
    Task_Actions_List,
    TaskCalendarPicker,
  },
  props: {
    row: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'submit', 'update_row'],
  data() {
    return {
      InstructionType,
      TaskStatus,
      NEEDS_DATE,
      selectedAction: '',
      additionalData: '',
      openHistory:false,
      can_submit:false,
      can_submit_message:'Must complete task action before submitting',
      field_errors:[],
      snoozeUntil: '',
      daysSelected: null,
      showCalendarPicker: false,
      unsnoozing: false,
      submitting: false,
    }
  },
  computed:{
    ...mapGetters('auth', ['isAuthenticated', 'current_user', 'loading']),
    hasWorkActivities() {
      if (!this.row || !this.row.task_type_name) return false
      const name = this.row.task_type_name.toLowerCase()
      return name.includes('suspect') || name.includes('early start') ||
             name.includes('claim') || name.includes('confirm') ||
             name.includes('1lr') || name.includes('2lr') || name.includes('3lr') ||
             name.includes('medication') || name.includes('care gap') ||
             name.includes('episource') || name.includes('reach out')
    },
    activeActions() {
      if (!this.row || !this.row.available_action_types) {
        return [];
      }

      return this.row.available_action_types.filter(action => {
        // Filter out inactive actions
        if (action.active === false) return false;

        // Only show unsnooze action (instruction_type_id = 7) when task is snoozed (status = 4)
        if (action.task_instruction_type_id === InstructionType.UNSNOOZE && this.row.status !== TaskStatus.SNOOZED) {
          return false;
        }

        // Only show snooze action when task is NOT snoozed
        if (action.task_instruction_type_id === InstructionType.SNOOZE && this.row.status === TaskStatus.SNOOZED) {
          return false;
        }

        // Only show "Place on Hold" when task is Active
        if (action.task_instruction_type_id === InstructionType.HOLD && this.row.status !== TaskStatus.ACTIVE) {
          return false;
        }

        // Only show "Release from Hold" when task is On Hold
        if (action.task_instruction_type_id === InstructionType.RELEASE_HOLD && this.row.status !== TaskStatus.ON_HOLD) {
          return false;
        }

        // Only show "Send Response to QA Reviewer" (instruction_type_id = 17)
        // on QA-returned tasks (metadata.reassignment_type === 'qa_returned')
        if (action.task_instruction_type_id === InstructionType.CODER_RESPONSE &&
            this.row?.metadata?.reassignment_type !== 'qa_returned') {
          return false;
        }

        return true;
      });
    },
    minSnoozeDate() {
      // Set minimum to 1 hour from now
      const date = new Date();
      date.setHours(date.getHours() + 1);
      return date.toISOString().slice(0, 16);
    },
    canSubmit() {
      // Default case - can submit if action is selected
      if (this.submitting) return false;
      if (!this.selectedAction) return false;

      // For snooze and update-due-date actions, also need a date
      if (NEEDS_DATE.includes(this.selectedAction.task_instruction_type_id)) {
        return !!this.snoozeUntil;
      }

      // For other actions, check the can_submit flag
      return this.can_submit;
    }
  },
  methods: {
    triggerUnsnooze() {
      // Find the Unsnooze action
      const unsnoozeAction = this.activeActions.find(
        action => action.task_instruction_type_id === InstructionType.UNSNOOZE
      );

      if (unsnoozeAction) {
        this.selectedAction = unsnoozeAction;
        // Auto-submit the unsnooze action
        this.submit();
      } else {
        this.field_errors = ['Unsnooze action not available'];
      }
    },
    formatDate,
    set_can_submit(value,message) {
      this.can_submit = value
      this.can_submit_message= message
      console.log(this.can_submit)
    },
    handleOwnerUpdate(owner) {
      // Handle owner update event from Task_Owner_Widget
      console.log('Task owner updated:', owner);
      this.$emit('update_row');
    },
    setQuickDate(days) {
      this.daysSelected = days;
      const d = new Date();
      d.setDate(d.getDate() + days);
      d.setHours(9, 0, 0, 0);
      const pad = n => String(n).padStart(2, '0');
      this.snoozeUntil = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    onCalendarPick(val) {
      this.snoozeUntil = val;
      this.daysSelected = null;
    },
    formatPickerDisplay(val) {
      if (!val) return '';
      try {
        return new Date(val).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
      } catch { return val; }
    },
    submit() {
      this.field_errors = []
      if (!this.selectedAction) {
        this.field_errors = ['Please select an action']
        return
      }

      // Build metadata based on action type
      const metadata = {
        'additionalData': this.additionalData,
      }

      // Add snooze_until for snooze actions
      if (this.selectedAction.task_instruction_type_id === InstructionType.SNOOZE) {
        if (!this.snoozeUntil) {
          this.field_errors = ['Please select a date to snooze until']
          return
        }
        metadata.snooze_until = new Date(this.snoozeUntil).toISOString()
      }

      // Add due_date for update-due-date actions
      if (this.selectedAction.task_instruction_type_id === InstructionType.UPDATE_DUE_DATE) {
        if (!this.snoozeUntil) {
          this.field_errors = ['Please select a new due date']
          return
        }
        metadata.due_date = new Date(this.snoozeUntil).toISOString()
      }

      // Set loading state for unsnooze actions
      if (this.selectedAction.task_instruction_type_id === InstructionType.UNSNOOZE) {
        this.unsnoozing = true;
      }

      this.submitting = true;
      submitAction({
        task_id: this.row.id,
        task_action_type_id: this.selectedAction.id,
        metadata: metadata
      })
        .then(result => {
          if (result.already_completed) {
            this.field_errors = ['This task was already completed']
            return
          }

          if (this.selectedAction.task_instruction_type_id === InstructionType.UNSNOOZE) {
            this.$emit('close');
            this.$emit('update_row');
          } else {
            this.$emit('submit')
          }
        })
        .catch(error => {
          console.error('Error:', error)
          const detail = error.response?.data?.error || error.message;
          this.field_errors = [detail || 'Failed to submit action']
        })
        .finally(() => {
          this.unsnoozing = false;
          this.submitting = false;
        })
    },
    close(){
      this.$emit('close')
    }
  },
}
</script>

<style scoped>
/* Custom scrollbar for content area */
.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: #CBD5E1 #F3F4F6;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 8px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #F3F4F6;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: #CBD5E1;
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background-color: #94A3B8;
}

/* Smooth animations */
@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.animate-slide-in {
  animation: slideIn 0.3s ease-out;
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
